import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTheme } from '@/hooks/useTheme';
import { useAuthStore } from '@/store/authStore';
import { projectMembershipService, CreateProjectMembershipData, UpdateProjectMembershipData, UpdateProjectMemberAdminData } from '@/services/projectMembershipService';
import { userService } from '@/services/userService';
import { projectService } from '@/services/projectService';
import { ProjectMembership, User, Project } from '@/types';
import { formatDate } from '@/utils';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { Toast } from '@/components/Toast';

const membershipSchema = z.object({
  user: z.string().min(1, 'L\'utilisateur est requis'),
  role: z.enum(['PROJECT_ADMIN', 'PROJECT_MANAGER', 'PROJECT_USER']),
});

const editMemberSchema = z.object({
  fullName: z.string().min(1, 'Le nom complet est requis'),
  email: z.string().email('Email invalide'),
  phone: z.string().optional(),
  isActive: z.boolean(),
  role: z.enum(['PROJECT_ADMIN', 'PROJECT_MANAGER', 'PROJECT_USER']),
  password: z.string().optional(),
  confirmPassword: z.string().optional(),
}).refine((data) => {
  // If password is provided, confirmPassword must match
  if (data.password && data.password.trim() !== '') {
    return data.password === data.confirmPassword;
  }
  return true;
}, {
  message: 'Les mots de passe ne correspondent pas',
  path: ['confirmPassword'],
}).refine((data) => {
  // If password is provided, it must meet requirements
  if (data.password && data.password.trim() !== '') {
    if (data.password.length < 9) {
      return false;
    }
    const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!#"%*?&])[A-Za-z\d@$!#"%*?&]{9,}$/;
    return passwordPattern.test(data.password);
  }
  return true;
}, {
  message: 'Le mot de passe doit contenir au moins 9 caractères, une minuscule, une majuscule, un chiffre et un caractère spécial (@$!#"%*?&)',
  path: ['password'],
});

type MembershipFormData = z.infer<typeof membershipSchema>;
type EditMemberFormData = z.infer<typeof editMemberSchema>;

const PROJECT_ROLES = [
  { value: 'PROJECT_ADMIN', label: 'Administrateur' },
  { value: 'PROJECT_MANAGER', label: 'Manager' },
  { value: 'PROJECT_USER', label: 'Utilisateur' },
] as const;

export function ProjectMembersPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { isDark, toggleTheme } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [editingMembership, setEditingMembership] = useState<ProjectMembership | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const projectIdNum = projectId ? parseInt(projectId, 10) : 0;

  const getUserInitials = () => {
    if (!user) return 'U';
    const nameParts = user.fullName?.split(' ') || [];
    return nameParts
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
    setValue,
  } = useForm<MembershipFormData>({
    resolver: zodResolver(membershipSchema),
    defaultValues: {
      role: 'PROJECT_USER',
    },
  });

  const {
    register: registerEdit,
    handleSubmit: handleSubmitEdit,
    control: controlEdit,
    reset: resetEdit,
    formState: { errors: errorsEdit },
    setValue: setValueEdit,
  } = useForm<EditMemberFormData>({
    resolver: zodResolver(editMemberSchema),
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      isActive: true,
      role: 'PROJECT_USER',
      password: '',
      confirmPassword: '',
    },
  });

  // Update form values when editingMembership changes
  useEffect(() => {
    if (editingMembership && isSidebarOpen) {
      const user = typeof editingMembership.user === 'object' ? editingMembership.user : null;
      if (user) {
        setValueEdit('fullName', user.fullName || '');
        setValueEdit('email', user.email || '');
        setValueEdit('phone', user.phone || '');
        setValueEdit('isActive', user.isActive ?? true);
        setValueEdit('role', editingMembership.role as 'PROJECT_ADMIN' | 'PROJECT_MANAGER' | 'PROJECT_USER');
        setValueEdit('password', '');
        setValueEdit('confirmPassword', '');
      }
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [editingMembership, isSidebarOpen, setValueEdit]);

  // Fetch project
  const { data: project } = useQuery({
    queryKey: ['project', projectIdNum],
    queryFn: () => projectService.getProject(projectIdNum),
    enabled: !!projectIdNum,
  });

  // Fetch memberships
  const {
    data: memberships,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['projectMemberships', projectIdNum],
    queryFn: async () => {
      const result = await projectMembershipService.getMembershipsByProject(projectIdNum);
      if (process.env.NODE_ENV === 'development') {
        console.log('ProjectMemberships fetched:', result);
        console.log('Project ID:', projectIdNum);
      }
      return result;
    },
    enabled: !!projectIdNum,
  });

  // Fetch all users for the select
  const { data: usersResponse } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => userService.getUsers({ page: 1, itemsPerPage: 1000 }),
  });

  const allUsers = usersResponse?.data || [];

  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [showToast, setShowToast] = useState(false);

  // Create membership mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateProjectMembershipData) => projectMembershipService.createMembership(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectMemberships', projectIdNum] });
      setToastMessage('Membre ajouté avec succès !');
      setToastType('success');
      setShowToast(true);
      setIsModalOpen(false);
      reset();
      setEditingMembership(null);
    },
    onError: (error: any) => {
      let errorMessage = 'Erreur lors de l\'ajout du membre';
      const data = error?.response?.data;
      if (data?.violations?.length && typeof data.violations[0]?.message === 'string') {
        errorMessage = data.violations[0].message;
      } else if (typeof data?.detail === 'string') {
        errorMessage = data.detail;
      } else if (typeof data?.message === 'string') {
        errorMessage = data.message;
      } else if (typeof error?.message === 'string') {
        errorMessage = error.message;
      }
      setToastMessage(errorMessage);
      setToastType('error');
      setShowToast(true);
    },
  });

  // Admin: update member (user fields + project role) in ONE call
  const updateMemberMutation = useMutation({
    mutationFn: ({ projectId, membershipId, data }: { projectId: number; membershipId: number; data: UpdateProjectMemberAdminData }) =>
      projectMembershipService.updateProjectMember(projectId, membershipId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectMemberships', projectIdNum] });
      setToastMessage('Membre modifié avec succès !');
      setToastType('success');
      setShowToast(true);
      setIsSidebarOpen(false);
      resetEdit();
      setEditingMembership(null);
      setShowPassword(false);
      setShowConfirmPassword(false);
    },
    onError: (error: any) => {
      console.error('Error updating member:', error);
      let errorMessage = 'Erreur lors de la modification';
      if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      setToastMessage(errorMessage);
      setToastType('error');
      setShowToast(true);
    },
  });

  const onSubmit = (data: MembershipFormData) => {
    if (editingMembership) {
      // Extract user and project IRIs from editingMembership
      const user = typeof editingMembership.user === 'object' && editingMembership.user !== null
        ? `/api/users/${editingMembership.user.id}`
        : typeof editingMembership.user === 'string'
          ? editingMembership.user
          : null;
      
      const project = typeof editingMembership.project === 'object' && editingMembership.project !== null
        ? `/api/projects/${editingMembership.project.id}`
        : typeof editingMembership.project === 'string'
          ? editingMembership.project
          : `/api/projects/${projectIdNum}`;

      if (!user || !project) {
        setToastMessage('Erreur: données du membership invalides');
        setToastType('error');
        setShowToast(true);
        return;
      }

      updateMutation.mutate({
        id: editingMembership.id,
        data: {
          user,
          project,
          role: data.role,
        },
      });
    } else {
      createMutation.mutate({
        user: data.user,
        project: `/api/projects/${projectIdNum}`,
        role: data.role,
      });
    }
  };

  const handleEdit = (membership: ProjectMembership) => {
    setEditingMembership(membership);
    setIsSidebarOpen(true);
    // Values will be set by useEffect when isSidebarOpen becomes true
  };

  const onSubmitEdit = async (data: EditMemberFormData) => {
    if (!editingMembership) return;

    const user = typeof editingMembership.user === 'object' ? editingMembership.user : null;
    if (!user || !user.id) {
      setToastMessage('Erreur: utilisateur introuvable');
      setToastType('error');
      setShowToast(true);
      return;
    }

    try {
      const payload: UpdateProjectMemberAdminData = {
        fullName: data.fullName,
        phone: data.phone || undefined,
        isActive: data.isActive,
        role: data.role,
      };

      // Password: only send if both are filled (backend enforces too)
      if ((data.password && data.password.trim() !== '') || (data.confirmPassword && data.confirmPassword.trim() !== '')) {
        if (!data.password || !data.confirmPassword) {
          setToastMessage('Veuillez saisir et confirmer le mot de passe.');
          setToastType('error');
          setShowToast(true);
          return;
        }
        if (data.password !== data.confirmPassword) {
          setToastMessage('Les mots de passe ne correspondent pas');
          setToastType('error');
          setShowToast(true);
          return;
        }
        payload.password = data.password;
        payload.confirmPassword = data.confirmPassword;
      }

      await updateMemberMutation.mutateAsync({
        projectId: projectIdNum,
        membershipId: editingMembership.id,
        data: payload,
      });
    } catch (error: any) {
      // handled by mutation onError too; keep for safety
      console.error('Unhandled error:', error);
    }
  };

  const handleNewMember = () => {
    setEditingMembership(null);
    reset();
    setIsModalOpen(true);
  };

  const getUserFromMembership = (membership: ProjectMembership): User | null => {
    if (typeof membership.user === 'object' && membership.user !== null) {
      return membership.user as User;
    }
    return null;
  };

  const getProjectFromMembership = (membership: ProjectMembership): Project | null => {
    if (typeof membership.project === 'object' && membership.project !== null) {
      return membership.project as Project;
    }
    return null;
  };

  if (!projectIdNum) {
    return <div className="p-6">Projet non trouvé</div>;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      {/* Sidebar */}
      <AdminSidebar 
        getUserInitials={getUserInitials} 
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        {/* Header */}
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-4 md:hidden">
              <button 
                onClick={() => setSidebarOpen(true)}
                className="text-slate-500 hover:text-slate-600 dark:text-slate-400 dark:hover:text-white"
              >
                <span className="material-icons-outlined">menu</span>
              </button>
              <span className="font-bold text-lg dark:text-white">GTB Admin</span>
            </div>
            <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
              <span className="text-slate-400 dark:text-slate-600">Admin</span> / Membres du Projet
            </div>
            {project && (
              <div className="hidden md:flex items-center gap-2">
                <span className="text-lg font-bold text-slate-900 dark:text-white">{project.title}</span>
                {project.city && (
                  <>
                    <span className="text-slate-400">•</span>
                    <span className="text-sm text-slate-500 dark:text-slate-400">{project.city}</span>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/admin/projects/${projectId}/edit`)}
              className="flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg hover:bg-slate-50 dark:hover:bg-[#1E293B] transition-colors"
            >
              <span className="material-icons-outlined">arrow_back</span>
              <span className="hidden sm:inline">Retour</span>
            </button>
            <button
              onClick={handleNewMember}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <span className="material-icons-outlined">add</span>
              <span className="hidden sm:inline">Ajouter un membre</span>
            </button>
            <button
              onClick={toggleTheme}
              className="p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors"
              title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? (
                <span className="material-icons-outlined">light_mode</span>
              ) : (
                <span className="material-icons-outlined">dark_mode</span>
              )}
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="text-center py-12">Chargement...</div>
          ) : error ? (
            <div className="text-center py-12 text-red-500">
              Erreur lors du chargement des membres
              {error instanceof Error && <div className="mt-2 text-xs">{error.message}</div>}
            </div>
          ) : !memberships || memberships.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              Aucun membre trouvé
              {process.env.NODE_ENV === 'development' && (
                <div className="mt-2 text-xs text-slate-400">
                  Project ID: {projectIdNum} | Memberships: {memberships?.length || 0}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white dark:bg-[#16202A] rounded-lg border border-slate-200 dark:border-[#1E293B] overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-[#1E293B]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Utilisateur
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Rôle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Ajouté le
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-[#1E293B]">
                  {memberships.map((membership) => {
                    const user = getUserFromMembership(membership);
                    if (!user) return null;

                    return (
                      <tr key={membership.id} className="hover:bg-slate-50 dark:hover:bg-[#1E293B]">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                              {user.fullName
                                .split(' ')
                                .map((n) => n[0])
                                .join('')
                                .toUpperCase()
                                .slice(0, 2)}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium">{user.fullName}</div>
                              <div className="text-sm text-slate-500">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{user.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs rounded ${
                              membership.role === 'PROJECT_ADMIN'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                : membership.role === 'PROJECT_MANAGER'
                                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                  : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            }`}
                          >
                            {PROJECT_ROLES.find((r) => r.value === membership.role)?.label || membership.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                          {formatDate(membership.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEdit(membership)}
                              className="text-primary hover:text-primary/80"
                              title="Modifier"
                            >
                              <span className="material-icons-outlined">edit</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Modal for adding new member */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsModalOpen(false);
              reset();
            }
          }}
        >
          <div
            className="bg-white dark:bg-[#16202A] rounded-lg shadow-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-slate-200 dark:border-[#1E293B] flex items-center justify-between">
              <h2 className="text-xl font-semibold">Ajouter un membre</h2>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  reset();
                }}
                className="text-slate-500 hover:text-slate-700"
              >
                <span className="material-icons-outlined">close</span>
              </button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Utilisateur *</label>
                <select
                  {...register('user')}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Sélectionner un utilisateur</option>
                  {allUsers
                    .filter((user) => !(user.roles ?? []).includes('ROLE_ROOT'))
                    .filter((user) => !memberships?.some((m) => {
                      const mUser = getUserFromMembership(m);
                      return mUser?.id === user.id;
                    }))
                    .map((user) => (
                      <option key={user.id} value={`/api/users/${user.id}`}>
                        {user.fullName} ({user.email})
                      </option>
                    ))}
                </select>
                {errors.user && <p className="text-red-500 text-sm mt-1">{errors.user.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Rôle *</label>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <select
                      {...field}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {PROJECT_ROLES.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  )}
                />
                {errors.role && <p className="text-red-500 text-sm mt-1">{errors.role.message}</p>}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-[#1E293B]">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    reset();
                  }}
                  className="px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg hover:bg-slate-50 dark:hover:bg-[#1E293B]"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                >
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Side menu for editing member */}
      {isSidebarOpen && editingMembership && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => {
              setIsSidebarOpen(false);
              resetEdit();
              setEditingMembership(null);
              setShowPassword(false);
              setShowConfirmPassword(false);
            }}
          />
          {/* Sidebar */}
          <div className="fixed inset-y-0 right-0 w-[600px] bg-white dark:bg-[#16202A] border-l border-slate-200 dark:border-[#1E293B] shadow-2xl z-50 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-[#1E293B] flex items-center justify-between bg-slate-50 dark:bg-[#0B1116]">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Modifier le membre
              </h2>
              <button
                onClick={() => {
                  setIsSidebarOpen(false);
                  resetEdit();
                  setEditingMembership(null);
                }}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors"
              >
                <span className="material-icons-outlined">close</span>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <form onSubmit={handleSubmitEdit(onSubmitEdit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">
                    Nom complet <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...registerEdit('fullName')}
                    type="text"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#16202A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  {errorsEdit.fullName && <p className="text-red-500 text-xs mt-1">{errorsEdit.fullName.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...registerEdit('email')}
                    type="email"
                    disabled
                    className="w-full px-3 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-slate-100 dark:bg-[#0B1116] text-slate-500 dark:text-slate-400 cursor-not-allowed"
                  />
                  {errorsEdit.email && <p className="text-red-500 text-xs mt-1">{errorsEdit.email.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">
                    Téléphone
                  </label>
                  <input
                    {...registerEdit('phone')}
                    type="text"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#16202A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  {errorsEdit.phone && <p className="text-red-500 text-xs mt-1">{errorsEdit.phone.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">
                    Rôle dans le projet <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="role"
                    control={controlEdit}
                    render={({ field }) => (
                      <select
                        {...field}
                        className="w-full px-3 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#16202A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        {PROJECT_ROLES.map((role) => (
                          <option key={role.value} value={role.value}>
                            {role.label}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  {errorsEdit.role && <p className="text-red-500 text-xs mt-1">{errorsEdit.role.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">
                    Mot de passe (laisser vide pour ne pas changer)
                  </label>
                  <div className="relative">
                    <input
                      {...registerEdit('password')}
                      type={showPassword ? 'text' : 'password'}
                      className="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#16202A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 dark:text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors"
                      tabIndex={-1}
                    >
                      <span className="material-icons-outlined text-lg">
                        {showPassword ? 'visibility_off' : 'visibility'}
                      </span>
                    </button>
                  </div>
                  {errorsEdit.password && <p className="text-red-500 text-xs mt-1">{errorsEdit.password.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">
                    Confirmer le mot de passe
                  </label>
                  <div className="relative">
                    <input
                      {...registerEdit('confirmPassword')}
                      type={showConfirmPassword ? 'text' : 'password'}
                      className="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#16202A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 dark:text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors"
                      tabIndex={-1}
                    >
                      <span className="material-icons-outlined text-lg">
                        {showConfirmPassword ? 'visibility_off' : 'visibility'}
                      </span>
                    </button>
                  </div>
                  {errorsEdit.confirmPassword && <p className="text-red-500 text-xs mt-1">{errorsEdit.confirmPassword.message}</p>}
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">
                    <Controller
                      name="isActive"
                      control={controlEdit}
                      render={({ field }) => (
                        <input
                          type="checkbox"
                          {...field}
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                        />
                      )}
                    />
                    Utilisateur actif
                  </label>
                </div>

                <div className="flex gap-3 pt-4 border-t border-slate-200 dark:border-[#1E293B]">
                  <button
                    type="button"
                    onClick={() => {
                      setIsSidebarOpen(false);
                      resetEdit();
                      setEditingMembership(null);
                    }}
                    className="flex-1 px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg hover:bg-slate-50 dark:hover:bg-[#1E293B] transition-colors text-slate-700 dark:text-slate-300"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={updateMemberMutation.isPending}
                    className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    {updateMemberMutation.isPending ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
}
